// frontend/pages/event.tsx

import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from "react-router-dom";
// import { toast } from 'react-hot-toast';
import { Button } from 'flowbite-react';

// --- Hooks and Context ---
import { useGlobalModalContext, MODAL_TYPES } from '../components/modals/GlobalModal';
import { useContextState } from '../context/Context';
import { useEventApi, useShared } from "../store";
import config from "../config";

// --- Presentational Component Imports ---
import SecondHeader from '../components/headers/SecondHeader';
import Loader from '../components/shared/Loader';
import Footer from '../components/footers/Footer';
import ApplicationBar from '../components/shared/ApplicationBar';
// import EventInfo from './EventInfo';
// import EventDetails from './eventDetails';
import EventPictures from '../components/events/EventPictures';
import ListWithIcons from '../components/shared/ListWithIcons';
import EventPromoters from '../components/events/EventPromoters';
import EventPricing from '../components/events/eventPricing';
import Tickets from '../components/tickets/Tickets';
import EventInfo from '../components/events/EventInfo'
// import TicketScanner from '../scan/TicketScanner';
import { EventTicketPackage } from '../models';

// --- Icons ---
// import { ImCancelCircle } from 'react-icons/im';
// import { BsQrCodeScan } from "react-icons/bs";

// --- Types ---
type View = 'event' | 'details' | 'guests' | 'requests' | 'requestApproved' | 'promoters' | 'edit' | 'packages' | 'pictures' | 'addImages' | 'tickets' | 'qrcode' | 'scan' | 'payment';
interface UiState {
  view: View;
  isLoading: boolean;
  isActionLoading: boolean;
}
interface ListItem { id: string; title: string; subtitle: string; originalGuest: { user: { id: string } } }

interface EventPageProps {
  /**
   * When true, this tells the component that a parent wrapper has already
   * fetched the event data and populated the context. The component should
   * skip its own data-fetching logic.
   */
  isDataPreloaded?: boolean;
  isTicket?:boolean;
}

// --- Main Page Component ---
export default function Event({ isDataPreloaded = false, isTicket=false }: EventPageProps) {
    
    // const router = useRouter();
    const [searchParams, setSearchParams] = useSearchParams();
    // const searchParams = searchParams();
    const eventIdFromUrl = searchParams.get('id');
    const packageCode = searchParams.get('package_code');
    
    const { showModal} = useGlobalModalContext();
    const { eventsCTX, accountCTX } = useContextState();
    const { signedIn } = accountCTX;
    // const { paymentState } = paymentCTX;
    const { allEventsState: { currentEventId, currentEvent }, eventPageType } = eventsCTX;
    const api = useEventApi();
    const [uiState, setUiState] = useState<UiState>({ view: 'event', isLoading: !isDataPreloaded, isActionLoading: false });
    const topRef = useRef<HTMLDivElement>(null);

    // --- FIX: Step 1 - Wrap the fetching logic in useCallback ---
    // We create a memoized function called `fetchEvent`. This function will only be
    // recreated if its own dependencies change, making it stable for the useEffect below.
    const fetchEvent = useCallback(() => {
        if (isDataPreloaded) {
            setUiState(prev => ({ ...prev, isLoading: false }));
            return;
        }

        const eventId = eventIdFromUrl || currentEventId;

        if (eventId) {
            // Only fetch if the event in context is not the one we want.
            if (currentEvent?.id !== eventId) {
                setUiState(prev => ({ ...prev, isLoading: true, view: 'event' }));
                api.getEvent(eventId).finally(() => {
                    setUiState(prev => ({ ...prev, isLoading: false }));
                });
            } else {
                // Event is already loaded
                setUiState(prev => ({ ...prev, isLoading: false }));
            }
        } else {
            // No event ID found in URL or context
            setUiState(prev => ({ ...prev, isLoading: false }));
        }
    }, [ currentEvent?.id, currentEventId, eventIdFromUrl, isDataPreloaded]);


    // --- FIX: Step 2 - Call the memoized function from useEffect ---
    // The useEffect now depends on the STABLE `fetchEvent` function.
    // This breaks the infinite loop because `fetchEvent` is no longer a new
    // function on every render.
    useEffect(() => {
        fetchEvent();
    }, [fetchEvent]);


    const processedGuestListItems = useMemo((): ListItem[] => {
        if (!currentEvent?.guests || currentEvent?.status !== 'upcoming') return [];
        // console.log('guests: ', currentEvent?.guests)
        return currentEvent.guests.map((guest: any) => ({
            id: guest.id, title: guest.username || 'Unknown User', subtitle: guest.status, icon:guest.icon, status:guest.status, originalGuest: guest,
        }));
    }, [currentEvent?.guests]);

    const filteredPackages = useMemo(() => {
        // 1. Add a guard clause for safety
        if (!currentEvent?.price) {
            return [];
        }

        // 2. Handle the case for event managers or ticket holders
        if (eventPageType === 'myevent' || eventPageType === 'myticket') {
            return currentEvent.price; // They should see all packages
        }
        
        // 3. Handle the public view logic with correct typing
        return currentEvent.price.filter((p: EventTicketPackage) => 
            // If a packageCode exists in the URL, show only that package
            packageCode ? p.code === packageCode : !p.code
            // Otherwise, show only packages that do NOT have a code
        );

    }, [currentEvent?.price, eventPageType, packageCode]);

    // --- Action Handlers (All logic is centralized here) ---
    const changeView = (newView: View) => {
      setUiState(prev => ({ ...prev, view: newView }));
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleProfileModal = (item: ListItem) => {
        const profileId = item.title; // title is username
        if (profileId) {
            showModal(MODAL_TYPES.PROFILE_MODAL, { profileType: 'guest', profileId });
        }
    };

    // const handleOpenModal = (type: 'getTicket' | 'editPackage' | 'addPackage' | 'editPromoter' | 'addPromoter', data?: any) => {
    //     if (!currentEvent) return;
    //     const baseProps = { eventId: currentEvent.id };
    //     switch(type) {
    //         case 'getTicket': showModal(MODAL_TYPES.GET_EVENT_PACKAGE_MODAL, { ...baseProps, eventPackage: data, eventName: currentEvent.name, premium: currentEvent.premium }); break;
    //         case 'editPackage': showModal(MODAL_TYPES.EDIT_EVENT_PACKAGE_MODAL, { ...baseProps, eventPackage: data }); break;
    //         case 'addPackage': showModal(MODAL_TYPES.EDIT_EVENT_PACKAGE_MODAL, baseProps); break;
    //         case 'editPromoter': showModal(MODAL_TYPES.EDIT_EVENT_PROMOTER_MODAL, { ...baseProps, eventPromoter: data }); break;
    //         case 'addPromoter': showModal(MODAL_TYPES.EDIT_EVENT_PROMOTER_MODAL, baseProps); break;
    //     }
    // };
    
    // --- Render Logic ---
    const renderHeader = () => {
        const headerProps = { callBack: (view: string) => changeView(view as View) };
        return <SecondHeader headerType='event' {...headerProps} />;
    };

    const renderMainContent = () => {
        if (uiState.isLoading) return <div className='mt-12'><Loader /></div>;
        if (!currentEvent) return <div className="py-20 text-center text-gray-500">Event not found.</div>;

        switch (uiState.view) {
            case 'event':
                return (
                    <center><EventInfo event={currentEvent}/></center>
                );
            
            case 'guests':
                return (
                    <div className="mt-12">
                        <ListWithIcons 
                            listItems={processedGuestListItems} 
                            statusFilter="active" 
                            titleKey="title" 
                            subtitleKey="subtitle" 
                            subtitleName="Status" 
                            onItemClick={handleProfileModal} 
                            itemOptions={[{ name: 'View Profile', onClick: handleProfileModal }]} 
                        />
                    </div>
                );

            case 'requests':
                return (
                    <div className="mt-16">
                        <ListWithIcons 
                            listItems={processedGuestListItems} 
                            statusFilter="request" 
                            titleKey="title" 
                            subtitleKey="subtitle" 
                            subtitleName="Status" 
                            onItemClick={handleProfileModal} 
                            itemOptions={[{ name: 'View Profile', onClick: handleProfileModal }]} 
                        />
                    </div>
                );
            
            case 'requestApproved':
                return (
                    <div className="mt-16">
                        <ListWithIcons
                            listItems={processedGuestListItems}
                            statusFilter="requestApproved" // Filter by the 'requestApproved' status
                            titleKey="title"
                            subtitleKey="subtitle"
                            subtitleName="Status"
                            onItemClick={handleProfileModal}
                            itemOptions={[{ name: 'View Profile', onClick: handleProfileModal }]}
                        />
                    </div>
                );

            case 'promoters':
                return (
                    <EventPromoters 
                        promoters={currentEvent.promoters || []} 
                        canManage={false} 
                        // onAddPromoter={() => handleOpenModal('addPromoter')} 
                        // onEditPromoter={(p) => handleOpenModal('editPromoter', p)} 
                        // onBlockPromoter={handleBlockPromoter} 
                        // onUnblockPromoter={handleUnblockPromoter} 
                    />
                );
            
            case 'packages':
                return (
                    <EventPricing 
                        eventStatus={currentEvent.status}
                        packages={filteredPackages} 
                        canManage={false} 
                        eventLink={currentEvent.link} 
                        // onAddPackage={() => handleOpenModal('addPackage')} 
                        // onEditPackage={(p) => handleOpenModal('editPackage', p)} 
                        // onDeletePackage={handleDeletePackage} 
                        // onGetTicket={(p) => handleOpenModal('getTicket', p)} 
                    />
                );
            
            case 'pictures':
                return (
                    <EventPictures 
                        images={currentEvent.images || []} 
                        canManage={false} 
                        onAddPictures={() => changeView('addImages')} 
                        // onDelete={handleDeleteImage} 
                    />
                );
           
            default:
                return <EventInfo event={currentEvent}/>; // Default to the main event view
        }
    };

    return (
        <div className="bg-white">
            <div ref={topRef} />
            {renderHeader()}
            <main className="min-h-screen px-4 mt-24">{renderMainContent()}</main>
            <Footer />
            <ApplicationBar />
        </div>
    );
}